import { forwardRef, MouseEvent, MutableRefObject, RefObject } from 'react';
import { useNavigate } from 'react-router-dom-v5-compat';

import { usePanZoomBoxControls } from 'dashboard/components-lib/PanZoomBox';
import { formatNumber } from 'dashboard/lib/utils/formatters';
import { InquiryTemplate } from 'dashboard/models/inquiry-template';
import { InquiryFunnelStepCard } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelStepCard';
import { FunnelNode } from 'dashboard/views/DashboardInquiryAnalyticsFunnelV2View/InquiryFunnelTree';

interface StepCardProps {
  currentStep: FunnelNode;
  currentPath: FunnelNode[];
  activeNodeToken: string;
  selectNode: (selectedNodeToken: string) => void;
  template: InquiryTemplate;
  selectedPath: FunnelNode[];
  onSelectPath: (
    event: MouseEvent,
    scrollRef: RefObject<HTMLDivElement>,
    selectedPath: FunnelNode[],
  ) => void;
  isAbsolute: boolean;
  inquiryTemplateVersionId: string;
}

const metricsTextForStep = (currentStep: FunnelNode, isAbsolute: boolean) => {
  const textParts = [];
  if (currentStep?.metrics?.value != null) {
    textParts.push(formatNumber(currentStep?.metrics?.value));
  }
  const ratio = isAbsolute ? currentStep?.pathRatio : currentStep?.relativeRatio;
  if (!isNaN(ratio)) {
    textParts.push(`${formatNumber(ratio * 100)}%`);
  }
  return textParts.join(' · ');
};

const metricsTextForStepDropOff = (
  currentStep: FunnelNode,
  currentPath: FunnelNode[],
  isAbsolute: boolean,
) => {
  if (currentStep.dropOffMetrics == null) {
    return null;
  }
  // we don't want the dropoff text if we have no siblings. we are the last element in the path, so we need
  // at least 2 elements to check our parent to see if we have a sibling.
  if (currentPath.length < 2) {
    return null;
  }
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  if (currentPath[currentPath.length - 2].branches.length === 1) {
    return null;
  }
  const textParts = [];
  textParts.push(formatNumber(currentStep.dropOffMetrics.value));
  const ratio = isAbsolute
    ? currentStep.dropOffMetrics.pathRatio
    : currentStep.dropOffMetrics.relativeRatio;
  if (!isNaN(ratio)) {
    textParts.push(`${formatNumber(ratio * 100)}%`);
  } else {
    textParts.push('N/A');
  }
  return textParts.join(' · ');
};

export const StepCardContainer = forwardRef<HTMLDivElement, StepCardProps>(
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  (props: StepCardProps, ref: MutableRefObject<HTMLDivElement>) => {
    const navigate = useNavigate();
    const {
      currentStep,
      currentPath,
      selectNode,
      template,
      onSelectPath,
      selectedPath,
      isAbsolute,
      inquiryTemplateVersionId,
    } = props;
    const active = selectedPath.find((step) => step.nodeToken === currentStep.nodeToken) != null;
    const { scrollRef } = usePanZoomBoxControls();
    return (
      <div
        ref={ref}
        onMouseEnter={(event) => {
          if (currentStep.isTerminal) {
            onSelectPath(event, scrollRef, currentPath);
          }
        }}
        onMouseLeave={(evt) => {
          if (currentStep.isTerminal) {
            onSelectPath(evt, scrollRef, []);
          }
        }}
        onClick={() => {
          selectNode(currentStep.token);
          if (currentStep.isTerminal) {
            navigate(
              `/dashboard/inquiry-analytics/${
                template.id
              }/funnel-flow/${inquiryTemplateVersionId}/${currentPath.map((step) => step.token).join(',')}`,
            );
          }
        }}
        id={currentStep.token}
      >
        {
          <InquiryFunnelStepCard
            isTerminal={currentStep.isTerminal}
            token={currentStep.token}
            type={currentStep.type}
            active={active}
            name={currentStep.name}
            condition={currentStep.parentCondition}
            /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
            dropOffMetricsText={metricsTextForStepDropOff(currentStep, currentPath, isAbsolute)}
            focused={false}
            metricText={metricsTextForStep(currentStep, isAbsolute)}
          />
        }
      </div>
    );
  },
);
